/* =========================================================
Mubkhar Bundle Builder — Enhanced with Bukhoor 1 Special Rules
========================================================= */

(function () {
  /**
   * DOM Ready helper
   */
  const ready = (fn) =>
    document.readyState === 'loading'
      ? document.addEventListener('DOMContentLoaded', fn)
      : fn();

  /**
   * Query selector helpers
   */
  const $1 = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  /**
   * Read bundle configuration
   */
  function readCFG() {
    try {
      return JSON.parse($1('#BundleConfig')?.textContent || '{}');
    } catch (e) {
      console.warn('[Bundle] Configuration parse error:', e);
      return {};
    }
  }

  /**
   * Expands a single slot config with max_qty > 1 into multiple individual slots.
   */
  function expandSlotsConfiguration(originalSlots) {
    const expandedSlots = [];
    originalSlots.forEach(slot => {
      const maxQty = Number(slot.max_qty || 1);
      if (maxQty <= 1) {
        expandedSlots.push({ ...slot, max_qty: 1 });
      } else {
        for (let i = 0; i < maxQty; i++) {
          expandedSlots.push({
            ...slot,
            key: `${slot.key}-${i}`,
            label_en: `${slot.label_en} - ${i + 1}`,
            label_ar: `${slot.label_ar} - ${i + 1}`,
            max_qty: 1,
            original_key: slot.key
          });
        }
      }
    });
    return expandedSlots;
  }

  /**
   * ENHANCED: Wait for image to be available with retry logic
   */
  function waitForImage(card, maxAttempts = 10, delay = 100) {
    return new Promise((resolve) => {
      let attempts = 0;

      const checkImage = () => {
        attempts++;
        const img = extractImageUrl(card);

        if (img || attempts >= maxAttempts) {
          resolve(img);
        } else {
          setTimeout(checkImage, delay);
        }
      };

      checkImage();
    });
  }

  /**
   * ENHANCED: Extract image URL from card
   */
  function extractImageUrl(card) {
    const images = $$('img', card);

    for (const img of images) {
      const computed = window.getComputedStyle(img);
      if (computed.display === 'none' || computed.visibility === 'hidden' || computed.opacity === '0') {
        continue;
      }

      let url = img.currentSrc || img.src || img.getAttribute('src');

      if (!url || url.includes('data:image') || url.includes('placeholder')) {
        url = img.getAttribute('data-src') ||
              img.getAttribute('data-srcset')?.split(' ')[0] ||
              img.getAttribute('srcset')?.split(' ')[0];
      }

      if (url && url.trim() && !url.includes('data:image') && !url.includes('placeholder')) {
        url = url.trim();
        if (url.startsWith('//')) {
          url = 'https:' + url;
        }
        if (url.startsWith('http') || url.startsWith('/')) {
          console.log('[Bundle] Found image URL:', url);
          return url;
        }
      }
    }

    const specificSelectors = [
      '.grid__image-ratio img',
      '.grid-product__image img',
      '.image-wrap img',
      '.card__media img',
      'picture source',
      'noscript img'
    ];

    for (const selector of specificSelectors) {
      const el = card.querySelector(selector);
      if (el) {
        let url = '';
        if (el.tagName === 'SOURCE') {
          url = el.getAttribute('srcset')?.split(' ')[0] || '';
        } else {
          url = el.getAttribute('src') || el.getAttribute('data-src') || '';
        }

        if (url && !url.includes('data:image') && !url.includes('placeholder')) {
          url = url.trim();
          if (url.startsWith('//')) url = 'https:' + url;
          if (url.startsWith('http') || url.startsWith('/')) {
            console.log('[Bundle] Found image via selector:', selector, url);
            return url;
          }
        }
      }
    }

    const noscript = card.querySelector('noscript');
    if (noscript) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = noscript.textContent;
      const noscriptImg = tempDiv.querySelector('img');
      if (noscriptImg) {
        let url = noscriptImg.getAttribute('src');
        if (url && !url.includes('placeholder')) {
          if (url.startsWith('//')) url = 'https:' + url;
          console.log('[Bundle] Found image in noscript:', url);
          return url;
        }
      }
    }

    const bgElements = [card, ...$$('[class*="image"]', card), ...$$('[class*="media"]', card)];
    for (const el of bgElements) {
      const bgImage = window.getComputedStyle(el).backgroundImage;
      if (bgImage && bgImage !== 'none') {
        const match = bgImage.match(/url\(['"]?([^'"]+)['"]?\)/);
        if (match && match[1] && !match[1].includes('placeholder')) {
          let url = match[1];
          if (url.startsWith('//')) url = 'https:' + url;
          console.log('[Bundle] Found background image:', url);
          return url;
        }
      }
    }

    console.warn('[Bundle] No image found for card:', card);
    return '';
  }

  /**
   * Get card data - async version
   */
  async function getCardDataAsync(card) {
    const pid = card.dataset.productId;
    const handle = card.dataset.productHandle;
    const tags = (card.dataset.productTags || '').split(',').map(s => s.trim()).filter(Boolean);
    const form = card.querySelector('form[action^="/cart/add"]') || card.querySelector('.grid-product__form');
    const variantId = form?.querySelector('input[name="id"]')?.value || '';
    const title = card.querySelector('.grid-product__title a, .grid-product__title')?.textContent?.trim() || handle || pid;

    const img = await waitForImage(card);

    // Enhanced price extraction - try multiple selectors
    let formattedPrice = '';
    let price = '0';

    const priceSelectors = [
      '.price__regular .price-item--regular',
      '.price__sale .price-item--sale',
      '[data-price]',
      '.price__regular .price-item',
      '.price-item',
      '.price',
      '.grid-product__price'
    ];

    for (const selector of priceSelectors) {
      const priceEl = card.querySelector(selector);
      if (priceEl) {
        formattedPrice = priceEl.textContent?.trim() || '';
        price = priceEl.dataset?.price || priceEl.textContent?.replace(/[^\d.]/g, '') || '0';

        if (formattedPrice) {
          console.log('[Bundle] 💰 Price extracted:', {
            selector: selector,
            formattedPrice: formattedPrice,
            rawPrice: price,
            product: handle
          });
          break;
        }
      }
    }

    return { pid, handle, tags, variantId, title, img, price, formattedPrice };
  }

  /**
   * Synchronous version for backward compatibility
   */
  function getCardData(card) {
    const pid = card.dataset.productId;
    const handle = card.dataset.productHandle;
    const tags = (card.dataset.productTags || '').split(',').map(s => s.trim()).filter(Boolean);
    const form = card.querySelector('form[action^="/cart/add"]') || card.querySelector('.grid-product__form');
    const variantId = form?.querySelector('input[name="id"]')?.value || '';
    const title = card.querySelector('.grid-product__title a, .grid-product__title')?.textContent?.trim() || handle || pid;

    const img = extractImageUrl(card);

    // Enhanced price extraction
    let formattedPrice = '';
    let price = '0';

    const priceSelectors = [
      '.price__regular .price-item--regular',
      '.price__sale .price-item--sale',
      '[data-price]',
      '.price__regular .price-item',
      '.price-item',
      '.price',
      '.grid-product__price'
    ];

    for (const selector of priceSelectors) {
      const priceEl = card.querySelector(selector);
      if (priceEl) {
        formattedPrice = priceEl.textContent?.trim() || '';
        price = priceEl.dataset?.price || priceEl.textContent?.replace(/[^\d.]/g, '') || '0';

        if (formattedPrice) {
          break;
        }
      }
    }

    return { pid, handle, tags, variantId, title, img, price, formattedPrice };
  }

  // Main execution
  ready(function () {
    const header = $1('[data-bundle-header]');
    if (!header) return;

    const CFG = readCFG();
    if (!CFG.bundle || !Array.isArray(CFG.slots)) {
      console.warn('[Bundle] Invalid configuration.');
      return;
    }

    CFG.slots = expandSlotsConfiguration(CFG.slots);
    const isAR = (CFG.locale || '').toLowerCase().startsWith('ar');

    // ===== BUKHOOR 1 DETECTION =====
    const bundleHandle = (CFG.bundle.handle || '').toLowerCase();
    const isBukhoor1 = bundleHandle.includes('bukhour-1') || bundleHandle.includes('bukhoor-1');

    console.log('[Bundle] Handle:', CFG.bundle.handle);
    console.log('[Bundle] Bukhoor 1 mode:', isBukhoor1);

    // --- STATE MANAGEMENT ---
    const SS_KEY = `bb:${CFG.bundle.id}`;
    const emptyState = { slots: {} };
    CFG.slots.forEach(s => { emptyState.slots[s.key] = []; });
    let STATE = loadState();

    function loadState() {
      try {
        const raw = sessionStorage.getItem(SS_KEY);
        if (!raw) return JSON.parse(JSON.stringify(emptyState));
        const parsed = JSON.parse(raw);
        const clean = { slots: {} };
        CFG.slots.forEach(s => {
          if (parsed.slots?.[s.key] && Array.isArray(parsed.slots[s.key])) {
            clean.slots[s.key] = parsed.slots[s.key];
          } else {
            clean.slots[s.key] = [];
          }
        });
        return clean;
      } catch {
        return JSON.parse(JSON.stringify(emptyState));
      }
    }

    function saveState() {
      sessionStorage.setItem(SS_KEY, JSON.stringify(STATE));
    }

    // ===== BUKHOOR 1: AUTO-FILL SLOT 2 AFTER SLOT 1 =====
    function autoFillSlot2() {
      if (!isBukhoor1 || !CFG.bundle.slot2_default) return;

      const slot2Key = CFG.slots[1]?.key;
      if (!slot2Key) {
        console.warn('[Bukhoor 1] No slot 2 found');
        return;
      }

      // Only fill if slot 2 is empty
      if (!STATE.slots[slot2Key] || STATE.slots[slot2Key].length === 0) {
        const defaultProduct = CFG.bundle.slot2_default;
        STATE.slots[slot2Key] = [{
          productId: String(defaultProduct.productId),
          variantId: String(defaultProduct.variantId),
          title: defaultProduct.title,
          img: defaultProduct.img,
          handle: defaultProduct.handle,
          tags: [],
          price: '15.000', // Original price for discount display
          formattedPrice: 'Free', // Display as free
          originalPrice: '15.000' // Keep track of original value
        }];
        saveState();
        console.log('[Bukhoor 1] ✅ Auto-filled slot 2 with FREE', defaultProduct.title);
      }
    }

    // ===== BUKHOOR 1: UPDATE BUNDLE PRICE WITH DISCOUNT DISPLAY =====
    function updateBundlePrice() {
      if (!isBukhoor1) return;

      const slot1Key = CFG.slots[0]?.key;
      const slot2Key = CFG.slots[1]?.key;
      const slot1Item = STATE.slots[slot1Key]?.[0];
      const slot2Item = STATE.slots[slot2Key]?.[0];

      console.log('[Bukhoor 1] 🔍 Price Update Check:');
      console.log('  Slot 1 Item:', slot1Item);
      console.log('  Slot 2 Item:', slot2Item);

      if (slot1Item) {
        const displayPrice = slot1Item.formattedPrice;
        const priceElements = [
          ...$$('.bbh__price'),
          ...$$('.bb-price-value')
        ];

        console.log('  Found', priceElements.length, 'price elements to update');

        priceElements.forEach(el => {
          // Show discount if both slots filled
          if (slot2Item && slot2Item.originalPrice) {
            const slot1Price = parseFloat(slot1Item.price) || 0;
            const slot2Price = parseFloat(slot2Item.originalPrice) || 15;
            const originalTotal = slot1Price + slot2Price;
            const originalFormatted = `${originalTotal.toFixed(3)} KD`;

            // Create discount display HTML
            el.innerHTML = `
              <span style="text-decoration: line-through; opacity: 0.6; font-size: 0.9em; margin-right: 8px;">${originalFormatted}</span>
              <span style="color: #d32f2f; font-weight: bold;">${displayPrice}</span>
              <span style="display: block; font-size: 0.85em; color: #388e3c; margin-top: 4px;">
                ${isAR ? `وفّر ${slot2Price.toFixed(3)} د.ك` : `Save ${slot2Price.toFixed(3)} KD`}
              </span>
            `;
            console.log('  ✅ Updated with discount:', el.className);
          } else {
            // No discount yet, just show price
            el.textContent = displayPrice;
            console.log('  ✅ Updated:', el.className, '→', displayPrice);
          }
        });

        console.log('[Bukhoor 1] ✅ Price updated to:', displayPrice);
      } else {
        console.log('[Bukhoor 1] ⏳ Slot 1 is empty');
      }
    }

    // --- FILTERING & SLOT LOGIC ---
    function getFirstUnfilledSlot() {
      return CFG.slots.find((slot, index) => {
        // DON'T skip slot 2 for Bukhoor 1 - it's now a regular required slot
        return (STATE.slots[slot.key]?.length || 0) < 1;
      });
    }

    /**
     * Count how many times a product is in the bundle across all slots
     */
    function countProductInBundle(productId) {
      let count = 0;
      Object.values(STATE.slots).forEach(slotItems => {
        if (Array.isArray(slotItems)) {
          slotItems.forEach(item => {
            if (item.productId === productId) count++;
          });
        }
      });
      return count;
    }

    /**
     * Find next available slot for a product based on its tags
     * Returns the next empty expanded slot that matches the product's tag
     */
    function findNextAvailableSlotKeyForTags(tags) {
      if (!Array.isArray(tags)) return null;

      const productTag = tags.find(tag => CFG.slots.some(s => s.filter_tag === tag.trim()));
      if (!productTag) return null;

      // Find the next empty slot with matching filter_tag
      const nextSlot = CFG.slots.find((slot, index) => {
        // DON'T skip slot 2 for Bukhoor 1 - it's now a regular required slot
        return slot.filter_tag === productTag && (STATE.slots[slot.key]?.length || 0) === 0;
      });

      return nextSlot ? nextSlot.key : null;
    }

    /**
     * Check if a product can still be added (has empty slots available)
     */
    function canAddMoreOfProduct(productId, tags) {
      const productTag = tags.find(tag => CFG.slots.some(s => s.filter_tag === tag.trim()));
      if (!productTag) return false;

      // Check if there's at least one empty slot with matching tag
      return CFG.slots.some((slot, index) => {
        // DON'T skip slot 2 for Bukhoor 1 - it's now a regular required slot
        return slot.filter_tag === productTag && (STATE.slots[slot.key]?.length || 0) === 0;
      });
    }

    function applyStepFiltering(activeTag = null) {
      let currentFilterTag = activeTag;
      if (!currentFilterTag) {
        const firstUnfilledSlot = getFirstUnfilledSlot();
        currentFilterTag = firstUnfilledSlot ? firstUnfilledSlot.filter_tag : null;
      }

      console.log('[Bundle] ===== FILTERING START =====');
      console.log('[Bundle] Active filter tag:', currentFilterTag);

      // Get ALL product cards directly (they're NOT inside .grid__item)
      const allProducts = $$('.grid-product');
      console.log('[Bundle] Total products found:', allProducts.length);

      if (!currentFilterTag) {
        console.log('[Bundle] No filter tag - hiding all products');
        allProducts.forEach(product => {
          product.style.setProperty('display', 'none', 'important');
        });
        syncActiveFilter(null);
        return;
      }

      let visibleCount = 0;
      let hiddenCount = 0;

      // Process each product directly
      allProducts.forEach((product, index) => {
        const productHandle = product.dataset.productHandle || 'unknown';
        const productTagsString = product.dataset.productTags || '';
        const productTags = productTagsString.split(',').map(tag => tag.trim()).filter(t => t);
        const shouldShow = productTags.includes(currentFilterTag);

        console.log(`[Bundle] Product ${index} (${productHandle}):`, {
          tags: productTags.slice(0, 3), // Show first 3 tags only
          filterTag: currentFilterTag,
          shouldShow: shouldShow
        });

        if (shouldShow) {
          product.style.removeProperty('display');
          product.classList.add('bb-filtered-in');
          product.classList.remove('bb-filtered-out');
          visibleCount++;
        } else {
          product.style.setProperty('display', 'none', 'important');
          product.classList.add('bb-filtered-out');
          product.classList.remove('bb-filtered-in');
          hiddenCount++;
        }
      });

      console.log('[Bundle] Filter complete - Visible:', visibleCount, 'Hidden:', hiddenCount);
      console.log('[Bundle] ===== FILTERING END =====');
      syncActiveFilter(currentFilterTag);
    }

    function renderStepFilters() {
      const container = $1('#bundle-step-filters');
      if (!container) return;

      container.innerHTML = '';
      const uniqueFilters = [...new Map(CFG.slots.map(item => [item.filter_tag, item])).values()];

      uniqueFilters.forEach(slot => {
        const label = isAR ? (slot.label_ar.split(' - ')[0] || slot.label_en.split(' - ')[0]) : (slot.label_en.split(' - ')[0] || slot.key);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'category-link';
        button.textContent = label;
        button.dataset.filterTag = slot.filter_tag;
        container.appendChild(button);
      });

      container.addEventListener('click', (e) => {
        const target = e.target.closest('[data-filter-tag]');
        if (target) {
          applyStepFiltering(target.dataset.filterTag);
        }
      });
    }

    function syncActiveFilter(activeTag) {
      $$('#bundle-step-filters .category-link').forEach(btn => {
        btn.classList.toggle('active', btn.dataset.filterTag === activeTag);
      });
    }

    // --- UI RENDERING ---
    function renderSlots() {
      const slotsContainer = $1('.bb-slots', header);
      if (!slotsContainer) return;
      slotsContainer.innerHTML = '';

      CFG.slots.forEach((slot, index) => {
        const el = document.createElement('div');
        el.dataset.slot = true;
        el.dataset.slotKey = slot.key;
        el.dataset.slotLabel = isAR ? slot.label_ar : slot.label_en;

        const sel = (STATE.slots[slot.key] || [])[0];

        if (sel) {
          el.className = 'bb-slot is-filled';
          el.innerHTML = `
            <div class="bb-slot__filled">
              <button type="button" class="bb-slot__remove" data-remove-slot="${slot.key}" data-product-id="${sel.productId}">×</button>
              <div class="bb-slot__thumb">${sel.img ? `<img src="${sel.img}" alt="${sel.title || ''}" loading="eager">` : ''}</div>
            </div>`;
        } else {
          el.className = 'bb-slot is-empty';
          el.innerHTML = `<div class="bb-slot__inner"><div class="bb-slot__placeholder" aria-hidden="true">+</div></div>`;
        }
        slotsContainer.appendChild(el);
      });

      const nextSlot = getFirstUnfilledSlot();
      $$('.bb-slots [data-slot]', header).forEach(el => {
        el.classList.remove('is-next');
        if (nextSlot && el.dataset.slotKey === nextSlot.key) {
          el.classList.add('is-next');
        }
      });

      const allRequiredFilled = CFG.slots.every((s, idx) => {
        // Skip slot 2 requirement for Bukhoor 1 (auto-filled)
        if (isBukhoor1 && idx === 1) return true;
        return !Number(s.required || 1) || (STATE.slots[s.key]?.length || 0) >= 1;
      });

      $$('[data-bundle-checkout]').forEach(cta => {
        cta.disabled = !allRequiredFilled;
        cta.setAttribute('aria-disabled', String(!allRequiredFilled));
      });

      // Update price for Bukhoor 1
      if (isBukhoor1) {
        updateBundlePrice();
      }

      syncSticky();
    }

    function setButtonState(card, productId, locked = false, ineligible = false) {
      const btn = card.querySelector('[data-bb-role="add"], .grid-product__cart-btn.add-to-cart, [type="submit"][name="add"]');
      if (!btn) return;

      card.classList.remove('bb-ineligible', 'bb-locked', 'bb-has-items');

      // Remove existing count badge if any
      const existingBadge = card.querySelector('.bb-product-count');
      if (existingBadge) existingBadge.remove();

      let text, isDisabled = false;

      if (ineligible) {
        card.classList.add('bb-ineligible');
        text = isAR ? 'غير متاح' : 'Not eligible';
        isDisabled = true;
      } else if (locked) {
        card.classList.add('bb-locked');
        text = isAR ? 'مُؤمَّن' : 'Locked';
        isDisabled = true;
      } else {
        const data = getCardData(card);
        const currentCount = countProductInBundle(productId);
        const canAddMore = canAddMoreOfProduct(productId, data.tags);

        if (currentCount > 0) {
          card.classList.add('bb-has-items');

          // Create and add count badge
          const countBadge = document.createElement('span');
          countBadge.className = 'bb-product-count';
          countBadge.textContent = currentCount;

          if (canAddMore) {
            text = isAR ? 'أضف عنصر إضافي' : 'Add Extra Item';
            isDisabled = false;
            // Insert badge before button
            btn.parentElement.insertBefore(countBadge, btn);
          } else {
            text = isAR ? 'تمت الإضافة' : 'Added';
            isDisabled = true;
            // Still show count even when maxed out
            btn.parentElement.insertBefore(countBadge, btn);
          }
        } else {
          text = isAR ? 'أضف إلى الحزمة' : 'Add to Bundle';
          isDisabled = false;
        }
      }

      btn.textContent = text;
      btn.disabled = isDisabled;
      btn.setAttribute('aria-disabled', String(isDisabled));
    }

    function syncGridButtons() {
      const activeSlot = getFirstUnfilledSlot();

      $$('.grid-product').forEach(card => {
        const data = getCardData(card);

        const slotForTag = CFG.slots.find(s => data.tags.includes(s.filter_tag));
        if (!slotForTag) {
          setButtonState(card, data.pid, false, true);
          return;
        }

        // Check if this slot category is active
        if (activeSlot && activeSlot.filter_tag !== slotForTag.filter_tag) {
          setButtonState(card, data.pid, true, false);
          return;
        }

        setButtonState(card, data.pid, false, false);
      });
    }

    // --- EVENT HANDLERS ---
    function handleRemoveClick(key, productId) {
      // Allow removing slot 2 in Bukhoor 1 mode (user can change selection)

      if (!key || !productId || !STATE.slots[key]) return;
      STATE.slots[key] = [];
      saveState();
      renderSlots();
      syncGridButtons();
      applyStepFiltering();
    }

    async function handleAddClick(e) {
      const btn = e.target.closest('[data-bb-role="add"], .grid-product__cart-btn.add-to-cart, [type="submit"][name="add"]');
      if (!btn || btn.disabled) return;
      e.preventDefault();
      e.stopPropagation();

      const card = btn.closest('.grid-product');
      if (!card) return;

      // Use async version to ensure we get the image and formatted price
      const data = await getCardDataAsync(card);
      const key = findNextAvailableSlotKeyForTags(data.tags);

      if (!key) {
        card.classList.add('bb-shake');
        setTimeout(() => card.classList.remove('bb-shake'), 500);
        return;
      }

      STATE.slots[key] = [{
        productId: data.pid,
        variantId: data.variantId,
        title: data.title,
        img: data.img,
        handle: data.handle,
        tags: data.tags,
        price: data.price,
        formattedPrice: data.formattedPrice
      }];

      const currentCount = countProductInBundle(data.pid);
      console.log('[Bundle] Added to slot:', key, data, 'Total count:', currentCount);
      saveState();

      // ===== BUKHOOR 1: AUTO-FILL SLOT 2 WHEN SLOT 1 IS FILLED =====
      if (isBukhoor1 && key === CFG.slots[0]?.key) {
        console.log('[Bukhoor 1] 🎯 Slot 1 filled! Auto-filling slot 2 and updating price...');

        // Auto-fill slot 2 with the free bukhoor box
        autoFillSlot2();

        // Update price display with discount info
        setTimeout(() => {
          updateBundlePrice();
          renderSlots();
          syncGridButtons();
          applyStepFiltering();
        }, 150);

        return; // Skip the render below since we're doing it in setTimeout
      }

      renderSlots();
      syncGridButtons();
      applyStepFiltering();
    }

    document.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('[data-remove-slot]');
      const stickyRemoveBtn = e.target.closest('[data-sticky-remove]');

      if (removeBtn) {
        handleRemoveClick(removeBtn.dataset.removeSlot, removeBtn.dataset.productId);
      } else if (stickyRemoveBtn) {
        const slotKey = stickyRemoveBtn.dataset.stickyRemove;
        const slotData = STATE.slots[slotKey];
        if (slotData && slotData[0]) {
          handleRemoveClick(slotKey, slotData[0].productId);
        }
      } else if (e.target.closest('[data-bb-role="add"], .grid-product__cart-btn.add-to-cart, [type="submit"][name="add"]')) {
        handleAddClick(e);
      }
    }, true);

    // --- STICKY HEADER ---
    const sticky = document.createElement('div');
    sticky.className = 'bb-sticky';
    document.body.appendChild(sticky);

    function buildStickyUI() {
      const slotsHtml = CFG.slots.map((s, index) => {
        const label = isAR ? s.label_ar : s.label_en;

        return `<div class="bb-step" data-slot-key="${s.key}">
                  <span class="bb-dot"></span>
                  <div class="bb-label">${label}</div>
                  <button type="button" class="bb-remove" data-sticky-remove="${s.key}" aria-label="Remove">
                    <svg width="12" height="13" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path fill-rule="evenodd" clip-rule="evenodd" d="M1.28949 1.46866H3.67318C3.67318 0.983583 3.60482 0.584919 3.92606 0.257636C4.08207 0.0986906 4.29765 0 4.53507 0H7.46493C7.93977 0 8.32682 0.394329 8.32682 0.878101V1.46866H11.6294C12.1235 1.46866 12.1235 2.23333 11.6294 2.23333H11.0858V10.9723C11.0858 12.0884 10.191 13 9.09552 13H2.90448C1.80901 13 0.914218 12.0884 0.914218 10.9723V2.23333H0.370595C-0.123532 2.23333 -0.123532 1.46866 0.370595 1.46866H1.28949ZM7.19489 4.1755V9.76141C7.19489 10.2648 7.94544 10.2648 7.94544 9.76141V4.1755C7.94544 3.67222 7.19489 3.67222 7.19489 4.1755ZM4.05456 4.1755V9.76141C4.05456 10.2648 4.80511 10.2648 4.80511 9.76141V4.1755C4.80511 3.67222 4.05456 3.67222 4.05456 4.1755ZM4.42373 1.46866H7.57613C7.57613 1.16579 7.64066 0.764672 7.46479 0.764672H4.53507C4.47465 0.764672 4.42373 0.816401 4.42373 0.878101V1.46866ZM7.95154 2.23347L1.66477 2.23333V10.9723C1.66477 11.6662 2.22343 12.2353 2.90448 12.2353H9.09552C9.77658 12.2353 10.3352 11.6662 10.3352 10.9723V2.23333L7.95154 2.23347Z" fill="#CB4B17"/>
                    </svg>
                  </button>
                </div>`;
      }).join('');

      const money = CFG?.bundle?.formatted_price || '0.00';
      sticky.innerHTML = `
        <div class="bb-sticky__wrap">
          <div class="bb-progress">${slotsHtml}</div>
          <div class="bb-sticky__price">
            <span class="bb-price-label">${isAR ? 'سعر الحزمة' : 'Bundle Price'}</span>
            <span class="bb-price-value">${money}</span>
          </div>
          <button type="button" class="bbh__checkout-link" data-bundle-checkout disabled>
            ${isAR ? 'الدفع' : 'Check Out'}
          </button>
        </div>`;
      sticky.style.setProperty('--bb-steps', CFG.slots.length);
      sticky.querySelector('[data-bundle-checkout]').addEventListener('click', handleCheckout);
    }

    function syncSticky() {
      const nextSlot = getFirstUnfilledSlot();
      $$('.bb-step', sticky).forEach((step, index) => {
        const key = step.dataset.slotKey;
        const isFilled = (STATE.slots[key]?.length || 0) > 0;

        step.classList.toggle('is-filled', isFilled);
        step.classList.toggle('is-next', nextSlot && key === nextSlot.key);

        // Show/hide remove button based on filled state
        const removeBtn = step.querySelector('.bb-remove');
        if (removeBtn) {
          if (isFilled) {
            removeBtn.removeAttribute('hidden');
            removeBtn.style.display = 'inline-flex';
          } else {
            removeBtn.setAttribute('hidden', '');
            removeBtn.style.display = 'none';
          }
        }
      });
    }

    const observer = new IntersectionObserver(([entry]) => {
      sticky.classList.toggle('is-visible', !entry.isIntersecting);
    }, { rootMargin: '-20px 0px 0px 0px', threshold: 0 });
    observer.observe(header);

    async function handleCheckout() {
      const checkoutButtons = $$('[data-bundle-checkout]');
      if (checkoutButtons[0]?.disabled) return;

      // Flatten all items from all slots
      const allItems = Object.values(STATE.slots).flat();

      if (allItems.length === 0) return;

      checkoutButtons.forEach(btn => {
        btn.textContent = isAR ? 'جاري المعالجة…' : 'Processing…';
        btn.disabled = true;
      });

      const resetButtons = () => {
        checkoutButtons.forEach(btn => {
          btn.textContent = isAR ? 'الدفع' : 'Check Out';
          btn.disabled = false;
        });
      };

      try {
        const variantId = Number(CFG?.bundle?.variant_id);
        if (!variantId) throw new Error('Missing bundle variant ID');

        const componentRefs = allItems.map(item => `gid://shopify/ProductVariant/${item.variantId}`);
        const properties = {
          '_component_references': JSON.stringify(componentRefs),
          '_bundle_configuration': CFG.bundle.title,
        };

        // ===== BUKHOOR 1: ADD CUSTOM PRICE =====
        if (isBukhoor1) {
          const slot1Item = allItems[0]; // First slot item
          if (slot1Item && slot1Item.price) {
            // Convert price to cents (smallest currency unit)
            // For KD (Kuwaiti Dinar) which has 3 decimal places, multiply by 1000
            const priceInCents = Math.round(parseFloat(slot1Item.price) * 1000);
            properties['_custom_price'] = String(priceInCents);

            console.log('[Bukhoor 1] 💰 Adding custom price to cart:', {
              formattedPrice: slot1Item.formattedPrice,
              rawPrice: slot1Item.price,
              priceInCents: priceInCents
            });
          }
        }
        // ===== END BUKHOOR 1 =====

        allItems.forEach((item, index) => {
          // Mark slot 2 as free for Bukhoor 1
          const isSlot2 = isBukhoor1 && index === 1;
          properties[`Component ${index + 1}`] = isSlot2 ? `${item.title} (Free)` : item.title;
        });

        const items = [{ id: variantId, quantity: 1, properties: properties }];

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items: items })
        });

        if (!res.ok) throw new Error('Failed to add bundle to cart');

        sessionStorage.removeItem(SS_KEY);
        window.location.href = '/cart';
      } catch (err) {
        console.error('[Bundle] Error:', err);
        alert(isAR ? 'تعذر إتمام الطلب. يرجى المحاولة مرة أخرى.' : 'Could not complete checkout. Please try again.');
        resetButtons();
      }
    }

    $$('[data-bundle-checkout]', header).forEach(btn => btn.addEventListener('click', handleCheckout));

    // --- FORCE IMAGE LOADING ON PAGE ---
    function forceLoadAllImages() {
      $$('.grid-product').forEach(card => {
        const images = $$('img', card);
        images.forEach(img => {
          if (img.loading === 'lazy') {
            img.loading = 'eager';
          }
          if (img.dataset.src && !img.src) {
            img.src = img.dataset.src;
          }
          if (img.dataset.srcset && !img.srcset) {
            img.srcset = img.dataset.srcset;
          }
        });
      });
    }

    // --- INITIALIZATION ---
    buildStickyUI();

    // DON'T pre-fill slot 2 for Bukhoor 1 - it will be auto-filled when slot 1 is added

    renderSlots();
    renderStepFilters();

    forceLoadAllImages();

    setTimeout(() => {
      applyStepFiltering();
      syncGridButtons();
      console.log(`[Bundle] Initialized with ${CFG.slots.length} individual slots after expansion.`);
      if (isBukhoor1) {
        console.log('[Bukhoor 1] ✅ Special mode active');
      }
    }, 300);
  });
})();